import React, { useEffect, useMemo, useRef, useState } from 'react';
import { roleVar } from './graphPalette';
import { useHighlight, skillMatchesTag } from '../context/Highlight';

/**
 * Self-contained 2D force-directed graph (no deps).
 * Role nodes (large) connect to the tech they used (small);
 * tech shared by several roles pulls those roles together.
 */
function shortCode(name) {
  const base = name.split('·')[0].replace(/Teli\.ai\s*—\s*/, '').trim();
  const words = base.split(/\s+/).filter(Boolean);
  if (words.length === 1 || words[0].length <= 2) return words[0].slice(0, 3);
  return words.map((w) => w[0]).join('').slice(0, 3).toUpperCase();
}

function buildGraph(items) {
  const nodes = [];
  const links = [];
  const tagIndex = new Map();
  items.forEach((it, i) => {
    nodes.push({ id: `role:${it.id}`, type: 'role', label: it.company, code: shortCode(it.company), item: it, r: 28, order: i, color: i });
    it.tags.forEach((t) => {
      if (!tagIndex.has(t)) {
        tagIndex.set(t, nodes.length);
        nodes.push({ id: `tag:${t}`, type: 'tag', label: t, r: 6, degree: 0, owners: [] });
      }
      const n = nodes[tagIndex.get(t)];
      n.degree += 1;
      n.owners.push(i);
      links.push({ source: `role:${it.id}`, target: `tag:${t}`, color: i });
    });
  });
  nodes.forEach((n) => { if (n.type === 'tag') { n.shared = n.owners.length > 1; n.r = n.shared ? 8 : 6; n.color = n.shared ? null : n.owners[0]; } });
  return { nodes, links };
}

export default function ExperienceGraph({ items, selectedId, onSelect, reached = 0 }) {
  const { skill: extSkill, role: extRole, setSkill, setRole } = useHighlight();
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [hover, setHover] = useState(null);
  const [, bump] = useState(0);

  const graph = useMemo(() => buildGraph(items), [items]);
  const sim = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      setSize({ w, h: w < 640 ? 460 : 600 });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const { w, h } = size;
    const roles = graph.nodes.filter((m) => m.type === 'role').length;
    const nodes = graph.nodes.map((n) => {
      const prev = sim.current?.nodes.find((p) => p.id === n.id);
      if (prev) return { ...n, x: prev.x, y: prev.y, vx: 0, vy: 0, fixed: prev.fixed };
      if (n.type === 'role') {
        const a = (n.order / roles) * Math.PI * 2 - Math.PI / 2;
        return { ...n, x: w / 2 + Math.cos(a) * Math.min(w, h) * 0.34, y: h / 2 + Math.sin(a) * Math.min(w, h) * 0.34, vx: 0, vy: 0 };
      }
      return { ...n, x: w / 2 + (Math.random() - 0.5) * w * 0.6, y: h / 2 + (Math.random() - 0.5) * h * 0.6, vx: 0, vy: 0 };
    });
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const links = graph.links.map((l) => ({ s: byId.get(l.source), t: byId.get(l.target), color: l.color }));
    sim.current = { nodes, links, byId, alpha: 1 };
  }, [graph, size]);

  useEffect(() => {
    let raf;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tick = () => {
      const s = sim.current;
      if (!s) return;
      const { nodes, links } = s;
      const { w, h } = size;
      const alpha = s.alpha;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const d2 = dx * dx + dy * dy || 0.01;
          const d = Math.sqrt(d2);
          const min = a.r + b.r + 26;
          const k = (a.type === 'role' && b.type === 'role' ? 26000 : 3800) / d2;
          let fx = (dx / d) * k, fy = (dy / d) * k;
          if (d < min) { fx += (dx / d) * (min - d) * 0.6; fy += (dy / d) * (min - d) * 0.6; }
          a.vx -= fx * alpha; a.vy -= fy * alpha; b.vx += fx * alpha; b.vy += fy * alpha;
        }
      }
      links.forEach(({ s: a, t: b }) => {
        const dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
        const rest = 95 + (b.degree > 1 ? 45 : 0);
        const f = (d - rest) * 0.05;
        const fx = (dx / d) * f, fy = (dy / d) * f;
        a.vx += fx * alpha; a.vy += fy * alpha; b.vx -= fx * alpha; b.vy -= fy * alpha;
      });
      nodes.forEach((n) => {
        n.vx += (w / 2 - n.x) * 0.0012 * alpha;
        n.vy += (h / 2 - n.y) * 0.0012 * alpha;
        if (!n.fixed) {
          n.vx *= 0.72; n.vy *= 0.72;
          n.x = Math.max(n.r + 10, Math.min(w - n.r - 10, n.x + n.vx));
          n.y = Math.max(n.r + 40, Math.min(h - n.r - 28, n.y + n.vy));
        } else { n.vx = 0; n.vy = 0; }
      });
      s.alpha = Math.max(0.02, s.alpha * 0.985);
      bump((x) => x + 1);
    };
    if (reduce) { for (let i = 0; i < 300; i++) tick(); return; }
    const loop = () => { tick(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  const drag = useRef(null);
  const toLocal = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const onDown = (n) => (e) => {
    e.preventDefault();
    const p = toLocal(e);
    drag.current = { n, dx: n.x - p.x, dy: n.y - p.y, moved: false };
    n.fixed = true;
    svgRef.current.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = toLocal(e);
    const { n, dx, dy } = drag.current;
    n.x = p.x + dx; n.y = p.y + dy;
    drag.current.moved = true;
    sim.current.alpha = Math.max(sim.current.alpha, 0.3);
  };
  const onUp = () => {
    if (!drag.current) return;
    const { n, moved } = drag.current;
    n.fixed = false;
    if (!moved && n.type === 'role') onSelect(n.item.id);
    drag.current = null;
  };

  const s = sim.current;
  const selected = selectedId ? `role:${selectedId}` : null;
  const extFocus = useMemo(() => {
    if (extRole) return `role:${extRole}`;
    if (extSkill) { const n = graph.nodes.find((m) => m.type === 'tag' && skillMatchesTag(extSkill, m.label)); return n ? n.id : null; }
    return null;
  }, [extSkill, extRole, graph]);
  const focus = hover || extFocus || selected;
  const neighbors = useMemo(() => {
    const m = new Map();
    graph.links.forEach((l) => {
      if (!m.has(l.source)) m.set(l.source, new Set());
      if (!m.has(l.target)) m.set(l.target, new Set());
      m.get(l.source).add(l.target); m.get(l.target).add(l.source);
    });
    return m;
  }, [graph]);
  const isLit = (id) => !focus || id === focus || neighbors.get(focus)?.has(id);
  const focusColor = (() => { const n = s?.byId.get(focus); return n && n.color != null ? roleVar(n.color) : 'var(--accent)'; })();

  return (
    <div className="xg" ref={wrapRef}>
      <svg ref={svgRef} className="xg__svg" width={size.w} height={size.h} viewBox={`0 0 ${size.w} ${size.h}`}
        onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        role="img" aria-label="Interactive graph of roles and the technologies used in each">
        <defs>
          <filter id="xg-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {s && s.links.map(({ s: a, t: b, color }, i) => {
          const lit = focus && (a.id === focus || b.id === focus);
          return (
            <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              className={`xg__link${lit ? ' xg__link--lit' : ''}${focus && !lit ? ' xg__link--dim' : ''}`}
              style={{ stroke: lit ? focusColor : roleVar(color) }} />
          );
        })}

        {s && s.nodes.map((n) => {
          const lit = isLit(n.id);
          const sel = n.id === selected;
          const col = n.color != null ? roleVar(n.color) : null;
          const cls = `xg__node xg__node--${n.type}${sel ? ' xg__node--selected' : ''}${lit ? '' : ' xg__node--dim'}${n.shared ? ' xg__node--shared' : ''}${n.type === 'role' && n.order < reached ? ' xg__node--reached' : ''}`;
          return (
            <g key={n.id} className={cls} transform={`translate(${n.x},${n.y})`} style={col ? { '--c': col } : undefined}
              onPointerDown={onDown(n)}
              onPointerEnter={() => { setHover(n.id); if (n.type === 'tag') setSkill(n.label); else setRole(n.item.id); }}
              onPointerLeave={() => { setHover(null); setSkill(null); setRole(null); }}>
              {n.type === 'role' ? (
                <>
                  {sel && <circle r={n.r + 14} className="xg__pulse" />}
                  <circle r={n.r + 6} className="xg__ring" />
                  <circle r={n.r} className="xg__core" filter={sel ? 'url(#xg-glow)' : undefined} />
                  <text dy="0.36em" textAnchor="middle" className="xg__code">{n.code}</text>
                  <text y={n.r + 24} textAnchor="middle" className="xg__label xg__label--role">{n.label}</text>
                </>
              ) : (
                <>
                  <circle r={n.r} className="xg__dot" />
                  {n.shared && <circle r={n.r + 3.5} className="xg__dot-ring" />}
                  <text y={n.r + 13} textAnchor="middle" className="xg__label xg__label--tag">{n.label}</text>
                </>
              )}
            </g>
          );
        })}
      </svg>

      <div className="xg__hint">drag · hover · click a role</div>
      <div className="xg__legend">
        {items.map((it, i) => (
          <button key={it.id} className={`xg__legend-item${selectedId === it.id ? ' on' : ''}`} style={{ '--c': roleVar(i) }} onClick={() => onSelect(it.id)}>
            <i />{it.company.replace(/Teli\.ai\s*—\s*/, '').split('·')[0].trim()}
          </button>
        ))}
        <span className="xg__legend-item xg__legend-item--shared"><i />shared tech</span>
      </div>
    </div>
  );
}
